import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Files, MediaDevices } from 'icons/untitledui';

import { isTouchDevice } from 'shared/helpers/device';
import { useShiftNoteCapture } from 'shiftNotes/helpers/ShiftNoteCaptureProvider';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';
import {
  AttachmentRejection,
  DOCUMENT_ACCEPT,
  isDocumentFile,
  isImageFile,
  isVideoFile,
  validateDocumentPicks,
  validateImagePicks,
  validateMixedPicks,
  validateVideoPick,
} from 'shiftNotes/helpers/noteCaptureAttachmentLimits';
import * as Buttons from 'theme/components/Buttons.style';
import { HiddenFileInput } from 'shiftNotes/styles/shared.style';

export type AttachmentPickerOrigin = 'text' | 'recording' | 'paused';

interface UseNoteAttachmentPickerOptions {
  fromState: AttachmentPickerOrigin;
  onBeforePick?: () => void;
}

interface UseNoteAttachmentPickerResult {
  attachmentError: AttachmentRejection | null;
  handleRemoveAttachment: (file: File) => void;
  controls: JSX.Element;
}

const getMediaType = (file: File) => {
  if (isDocumentFile(file)) return 'document';
  if (isVideoFile(file)) return 'video';
  return 'image';
};

export default function useNoteAttachmentPicker({
  fromState,
  onBeforePick,
}: UseNoteAttachmentPickerOptions): UseNoteAttachmentPickerResult {
  const { t } = useTranslation('pageShiftNotes');
  const { attachments, setAttachments, canAttachDocuments } =
    useShiftNoteCapture();
  const shiftNotesCapture = usePosthogCapture();
  const [attachmentError, setAttachmentError] =
    useState<AttachmentRejection | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);

  const canCaptureFromCamera = isTouchDevice();

  const hasVideo = attachments.some(isVideoFile);
  const hasImages = attachments.some(isImageFile);

  const openPicker = (ref: React.RefObject<HTMLInputElement>) => {
    onBeforePick?.();
    ref.current?.click();
  };

  const readAndClear = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    return picked;
  };

  const captureAttachmentsAdded = (files: File[]) => {
    files.forEach((file) =>
      shiftNotesCapture.captureAttachmentAdded({
        media_type: getMediaType(file),
        from_state: fromState,
      })
    );
  };

  const replaceVideo = (video: File) =>
    setAttachments((prev) => [...prev.filter(isDocumentFile), video]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = readAndClear(e);
    if (picked.length === 0) return;
    const { accepted, rejection } = validateImagePicks(picked, attachments);
    setAttachmentError(rejection);
    if (accepted.length === 0) return;
    setAttachments((prev) => [...prev, ...accepted]);
    captureAttachmentsAdded(accepted);
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = readAndClear(e);
    if (picked.length === 0) return;
    const { accepted, rejection } = validateVideoPick(picked[0], attachments);
    setAttachmentError(rejection);
    if (accepted.length === 0) return;
    replaceVideo(accepted[0]);
    captureAttachmentsAdded(accepted);
  };

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = readAndClear(e);
    if (picked.length === 0) return;
    const { accepted, rejection } = validateMixedPicks(picked, attachments);
    setAttachmentError(rejection);
    if (accepted.length === 0) return;
    if (isVideoFile(accepted[0])) {
      replaceVideo(accepted[0]);
      captureAttachmentsAdded([accepted[0]]);
      return;
    }
    setAttachments((prev) => [
      ...prev.filter((f) => !isVideoFile(f)),
      ...accepted,
    ]);
    captureAttachmentsAdded(accepted);
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = readAndClear(e);
    if (picked.length === 0) return;
    const { accepted, rejection } = validateDocumentPicks(picked, attachments);
    setAttachmentError(rejection);
    if (accepted.length === 0) return;
    setAttachments((prev) => [...prev, ...accepted]);
    captureAttachmentsAdded(accepted);
  };

  const handleRemoveAttachment = useCallback(
    (file: File) => {
      setAttachments((prev) => prev.filter((f) => f !== file));
      setAttachmentError(null);
      shiftNotesCapture.captureAttachmentRemoved({
        media_type: getMediaType(file),
        from_state: fromState,
      });
    },
    [fromState, setAttachments, shiftNotesCapture]
  );

  const documentControls = (
    <>
      <Buttons.SecondaryLightIcon
        $size="medium"
        onClick={() => openPicker(documentInputRef)}
        aria-label={t('noteCaptureAttachments.addFile')}
        data-testid="note-capture-file-button"
      >
        <Files.FileAttachment02 />
      </Buttons.SecondaryLightIcon>
      <HiddenFileInput
        ref={documentInputRef}
        accept={DOCUMENT_ACCEPT}
        multiple
        onChange={handleDocumentChange}
        data-testid="note-capture-file-input"
      />
    </>
  );

  const mediaControls = canCaptureFromCamera ? (
    <>
      <Buttons.SecondaryLightIcon
        $size="medium"
        onClick={() => openPicker(imageInputRef)}
        disabled={hasVideo}
        aria-label={t('noteCaptureAttachments.addPhoto')}
        data-testid="note-capture-photo-button"
      >
        <MediaDevices.Camera01 />
      </Buttons.SecondaryLightIcon>
      <Buttons.SecondaryLightIcon
        $size="medium"
        onClick={() => openPicker(videoInputRef)}
        disabled={hasImages}
        aria-label={t('noteCaptureAttachments.addVideo')}
        data-testid="note-capture-video-button"
      >
        <MediaDevices.VideoRecorder />
      </Buttons.SecondaryLightIcon>
      <HiddenFileInput
        ref={imageInputRef}
        accept="image/*"
        capture="environment"
        multiple
        onChange={handleImageChange}
        data-testid="note-capture-photo-input"
      />
      <HiddenFileInput
        ref={videoInputRef}
        accept="video/*"
        capture="environment"
        onChange={handleVideoChange}
        data-testid="note-capture-video-input"
      />
    </>
  ) : (
    <>
      <Buttons.SecondaryLightIcon
        $size="medium"
        onClick={() => openPicker(mediaInputRef)}
        aria-label={t('noteCaptureAttachments.addMedia')}
        data-testid="note-capture-media-button"
      >
        <MediaDevices.ImagePlus />
      </Buttons.SecondaryLightIcon>
      <HiddenFileInput
        ref={mediaInputRef}
        accept="image/*,video/*"
        multiple
        onChange={handleMediaChange}
        data-testid="note-capture-media-input"
      />
    </>
  );

  const controls = (
    <>
      {mediaControls}
      {canAttachDocuments && documentControls}
    </>
  );

  return { attachmentError, handleRemoveAttachment, controls };
}
